import { AdminInsurerAttention } from '@/types/admin-insurer-attention';
import { useTheme } from '@/shared/providers/theme';
import { Checkbox, Empty, TableProps } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { EMPTY_TEXT_FILTERED } from '@/constants';
import { useNavigate, useParams } from 'react-router-dom';
import { AppTable } from '../atoms';
import AppButton from '../atoms/AppButton';
import { EditFill } from '../atoms/AppIcon';

interface InsurerAttentionTableProps {
  data: AdminInsurerAttention[];
  selectedRowKeys: number[];
  sortInfo?: {
    columnKey?: string;
    order?: 'ascend' | 'descend';
  };
  onTableChange?: (
    sorter: SorterResult<AdminInsurerAttention> | SorterResult<AdminInsurerAttention>[]
  ) => void;
  onSelectionChange?: (selectedKeys: number[], selectedRows: AdminInsurerAttention[]) => void;
  onReflect?: (record: AdminInsurerAttention) => void;
  isLoading?: boolean;
}

const InsurerAttentionTable = ({
  data,
  selectedRowKeys,
  sortInfo,
  onTableChange,
  onSelectionChange,
  onReflect,
  isLoading,
}: InsurerAttentionTableProps) => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const navigate = useNavigate();
  const { colors } = useTheme();

  const columns: TableProps<AdminInsurerAttention>['columns'] = [
    {
      title: '県No',
      dataIndex: 'prefecture_id',
      key: 'prefecture_id',
      sorter: true,
      sortOrder: sortInfo?.columnKey === 'prefecture_id' ? sortInfo.order : undefined,
      width: '8%',
      render: (value: number | null) => (value != null ? String(value).padStart(2, '0') : '-'),
    },
    {
      title: '都道府県',
      dataIndex: 'prefecture_name',
      key: 'prefecture_name',
      width: '12%',
    },
    {
      title: '保険者名称',
      dataIndex: 'insurer_name',
      key: 'insurer_name',
      sorter: true,
      sortOrder: sortInfo?.columnKey === 'insurer_name' ? sortInfo.order : undefined,
    },
    {
      title: '保険者番号',
      dataIndex: 'insurer_number',
      key: 'insurer_number',
      sorter: true,
      sortOrder: sortInfo?.columnKey === 'insurer_number' ? sortInfo.order : undefined,
      width: '15%',
    },
    {
      title: '種別',
      dataIndex: 'insurer_type_name',
      key: 'insurer_type_name',
      width: '12%',
    },
    {
      title: '操作',
      key: 'action',
      width: '22%',
      render: (_, record) => (
        <div className="flex gap-2">
          <AppButton
            icon={<EditFill />}
            type="primary"
            onClick={() => navigate(`/${currentSite}/admin/insurer_attentions/${record.id}/edit`)}
          >
            編集
          </AppButton>
          <AppButton
            type="primary"
            onClick={() => onReflect?.(record)}
            style={
              {
                backgroundColor: colors.secondary,
                ['--action-bg-hover' as string]: colors.secondaryHover,
              } as React.CSSProperties
            }
          >
            データ反映
          </AppButton>
        </div>
      ),
    },
  ];

  const allSelected = data.length > 0 && selectedRowKeys.length >= data.length;

  return (
    <AppTable
      columns={columns}
      dataSource={data}
      rowKey="id"
      rowSelection={{
        align: 'left',
        type: 'checkbox',
        selectedRowKeys,
        onChange: (keys, rows) =>
          onSelectionChange?.(keys as number[], rows as AdminInsurerAttention[]),
        columnTitle: (
          <Checkbox
            checked={allSelected}
            onChange={e => {
              if (e.target.checked) {
                onSelectionChange?.(
                  data.map(r => r.id),
                  data
                );
              } else {
                onSelectionChange?.([], []);
              }
            }}
          />
        ),
      }}
      onChange={(_pagination, _filters, sorter) =>
        onTableChange?.(
          sorter as SorterResult<AdminInsurerAttention> | SorterResult<AdminInsurerAttention>[]
        )
      }
      loading={isLoading}
      locale={{
        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />,
      }}
    />
  );
};

export default InsurerAttentionTable;
